import { useEffect, useState  } from "react";
import { useNavigate } from "react-router-dom";

// Material UI imports
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import Paper from "@mui/material/Paper";
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import ListItemButton from '@mui/material/ListItemButton';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useMediaQuery, useTheme } from '@mui/system';

// Local imports
import Error from "../Error/Error";
import Loading from "../Loading";
import Notification from "../Notification";
import { api } from "../../api";
import { useTranslation } from 'react-i18next';

const SurveyTopics = () => {
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [checked, setChecked] = useState([]);
    const [alert, setAlert] = useState(false);
    const [alertMsg, setAlertMsg] = useState('');
    const [activeTooltip, setActiveTooltip] = useState(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    useEffect(() => {
        const fetchTopics = async () => {
            try {
                const data = await api.get('/survey/topics');
                setTopics(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }
        fetchTopics();
    }, []);

    const handleToggleTooltip = (topicId) => {
        if (topicId === activeTooltip) {
            setActiveTooltip(null);
        } else {
            setActiveTooltip(topicId);
        }
    }

    const handleCloseTooltip = () => {
        setActiveTooltip(null);
    }

    const handleToggle = (val) => () => {
        if (checked.length >= 3 && !checked.includes(val)) {
            setAlert(true);
            setAlertMsg(t('survey.alert_1'));
            return;
        }

        const currentIndex = checked.indexOf(val);
        const newChecked = [...checked];
        if (currentIndex === -1) {
            newChecked.push(val);
        } else {
            newChecked.splice(currentIndex, 1);
        }
        setChecked(newChecked);
    };

    const onClose = () => {
        setAlert(false);
        setAlertMsg('');
    }

    const submitForm = async (e) => {
        e.preventDefault();
        if (checked.length < 3) {
            setAlert(true);
            setAlertMsg(t('survey.alert_2'));
            return;
        }
        try {
            const resp = await api.post('/survey/topics', { selectedTopics: checked });
            if (resp) {
                // Navigate to the first question
                navigate('/survey/question?idx=1', {
                    state: {
                        severity: 'success',
                        message: t('survey.success_msg'),
                    },
                });
            }
        } catch (err) {
            console.error(err);
            setError(err.message);
        }
    }

    if (error) {
        return <Error message={error} />;
    }

    if (loading) {
        return <Loading />;
    }

    return (
        <Fade in timeout={1000}>
            <div className="main">
                <Typography variant={isMobile ? "h5" : "h4"} sx={{ textAlign: 'center', mb: 2 }}>{t('survey.title')}</Typography>
                <Paper elevation={isMobile ? 0 : 3} sx={{ padding: { xs: 2, sm: 4, md: 6 }, mb: 2, width: { xs: '100%', sm: '90%', md: '80%' }, margin: 'auto' }}>
                    <Box component="img" src="/images/survey_img.webp" alt="Animated image of a family" sx={{ display: 'block', width: '100%', maxWidth: '1000px', height: 'auto', margin: '1rem auto' }} />
                    <Typography variant="h6">{t('survey.description')}</Typography>
                    <List sx={{ mb: 2, listStyle: 'decimal', pl: {xs: 2, sm: 4}, borderBottom: '1px solid #ccc', pb: 2 }}>
                        <ListItem sx={{ display: 'list-item' }}>
                            <ListItemText primary={t('survey.instruction_1_title')} secondary={t('survey.instruction_1_description')} />
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <ListItemText primary={t('survey.instruction_2_title')} secondary={t('survey.instruction_2_description')} />
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <ListItemText primary={t('survey.instruction_3_title')} secondary={t('survey.instruction_3_description')} />
                            <List sx={{ listStyle: 'disc', ml: 2, pt: 0 }}>
                                <ListItem sx={{ display: 'list-item', py: 0 }}>
                                    <ListItemText secondary={t('survey.instruction_3_description_1')} />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item', py: 0 }}>
                                    <ListItemText secondary={t('survey.instruction_3_description_2')} />
                                </ListItem>
                            </List>
                        </ListItem>
                    </List>

                    <Paper elevation={4} sx={{ padding: 2, mt: 4, backgroundColor: 'rgb(228, 224, 238)', border: '2px solid rgb(91, 59, 140)' }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>{t('survey.select_topics')}</Typography>

                        {/* Alert */}
                        {alert && (
                            <Notification severity="warning" message={alertMsg} onClose={onClose} scroll={true} />
                        )}

                        {/* Topics List */}
                        { topics.length > 0 ? (
                            <form method="post" onSubmit={submitForm}>
                                <List sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                    {topics.map((topic) => {
                                        const labelId = `checkbox-list-label-${topic.id}`;
                                        const isChecked = checked.includes(topic.id);
                                        return (
                                            <ListItem 
                                                key={topic.id} 
                                                disablePadding 
                                                sx={{ width: { xs: '100%', sm: '45%', md: '30%' }, height: { xs: '12rem', sm: '15rem' }, borderRadius: '10px', paddingBottom: 2, mx: 1, backgroundColor: isChecked ? 'rgba(91, 59, 140, 0.3)' : '#ffff' }}
                                            >
                                                {isTouchDevice ? (
                                                     <ListItemButton 
                                                        role={undefined} 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleToggle(topic.id)();
                                                            handleToggleTooltip(topic.id); // Open tooltip on touch screen devices
                                                        }}
                                                        dense 
                                                        autoFocus="true" 
                                                        sx={{ height: '100%', display: 'flex', justifyContent: 'space-between'}}
                                                    >
                                                        <Tooltip
                                                            open={topic.id === activeTooltip && isChecked}
                                                            onClose={handleCloseTooltip}
                                                            title={t(`survey.question_${topic.id}`)} 
                                                            placement="top-start" 
                                                            arrow
                                                        >
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', alignSelf: 'center', height: '100%', width: '100%' }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', flex: 1}}>
                                                                    <ListItemText id={labelId} primary={t(`survey.topic_${topic.id}`)} sx={{ textAlign: 'center'}} />
                                                                    <Checkbox
                                                                        edge="end"
                                                                        checked={isChecked}
                                                                        tabIndex={-1}
                                                                        disableRipple
                                                                    />
                                                                </Box>
                                                                <Box component="img" src={topic.imageUrl} alt={t(`survey.topic_${topic.id}`)} sx={{ flex: 3, height: { xs: '80%', sm: '170px' }, width: '12rem', maxWidth: { xs: '100%', md: '90%'}, objectFit: 'cover', borderRadius: '10px' }} loading="lazy" />                                                    
                                                            </Box>
                                                        </Tooltip>
                                                    </ListItemButton>
                                                    ) : (
                                                    <ListItemButton 
                                                        role={undefined} 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleToggle(topic.id)();
                                                        }}
                                                        dense 
                                                        autoFocus="true" 
                                                        sx={{ height: '100%', display: 'flex', justifyContent: 'space-between'}}
                                                    >
                                                        <Tooltip 
                                                            title={t(`survey.question_${topic.id}`)} 
                                                            placement="top-start" 
                                                            arrow
                                                        >
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', alignSelf: 'center', height: '100%', width: '100%' }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', flex: 1}}>
                                                                    <ListItemText id={labelId} primary={t(`survey.topic_${topic.id}`)} sx={{ textAlign: 'center'}} />
                                                                    <Checkbox
                                                                        edge="end"
                                                                        checked={isChecked}
                                                                        tabIndex={-1}
                                                                        disableRipple
                                                                    />
                                                                </Box>
                                                                <Box component="img" src={topic.imageUrl} alt={t(`survey.topic_${topic.id}`)} sx={{ flex: 3, height: { xs: '80%', sm: '170px' }, width: '12rem', maxWidth: { xs: '100%', md: '90%'}, objectFit: 'cover', borderRadius: '10px' }} loading="lazy" />                                                    
                                                            </Box>
                                                        </Tooltip>
                                                    </ListItemButton>
                                                    )}
                                            </ListItem>
                                        )
                                    })}
                                </List>
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                    <Button variant="contained" type="submit">{t('survey.next')}</Button>
                                </Box>
                            </form>
                        ) : "No topics found." }
                    </Paper>
                </Paper>
            </div>
        </Fade>

    )
}

export default SurveyTopics;